import { Content } from "@/src/components/wrappers/Content";
import { useLocation } from "react-router-dom";
import { CustomersTable } from "./tables/CustomersTable";
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
} from "@/src/components/ui/navigation-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/src/components/ui/select";
import { ActionsContext, useEmit } from "@/src/lib/actions";
import { useEffect, useState } from "react";
import { OperatorsTable } from "./tables/OperatorsTable";
import { FiltersButton } from "./FiltersButton";
import { ExportButton } from "./ExportButton";
import { AddPaymentButton } from "./AddPaymentButton";
import OrderBySelect from "./OrderBySelect";
import { PayerType } from "./model";

export function Payments() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<"customers" | "operators">("customers");
  const [shouldRefetch, setShouldRefetch] = useState(false);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const type = queryParams.get("type") || "customers";

    if (type === "operators") {
      setActiveTab("operators");
    } else {
      setActiveTab("customers");
    }
  }, [location.search]);

  useEffect(() => {
    if (shouldRefetch) {
      setShouldRefetch(false);
    }
  }, [shouldRefetch]);

  const handlePaymentAdded = () => {
    setShouldRefetch(true);
  };

  return (
    <ActionsContext>
      <Content title="Payments" contentAction={<AddPaymentButton onPaymentAdded={handlePaymentAdded} />}>
        <div className="mb-2">
          <NavigationMenu>
            <NavigationMenuList className="bg-slate-100 border-0">
              <NavigationMenuItem className="!m-0">
                <NavigationMenuLink
                  href="/payments?type=customers"
                  active={activeTab === "customers"}
                  className="data-[active]:bg-white hover:bg-zinc-50 focus:bg-zinc-50 m-1.5"
                >
                  Customers
                </NavigationMenuLink>
              </NavigationMenuItem>
              <NavigationMenuItem className="!m-0">
                <NavigationMenuLink
                  href="/payments?type=operators"
                  active={activeTab === "operators"}
                  className="data-[active]:bg-white hover:bg-zinc-50 focus:bg-zinc-50 m-1.5"
                >
                  Operators
                </NavigationMenuLink>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </div>
        <div className="mb-2 flex justify-start items-center gap-2">
          <FiltersButton activeTab={activeTab} />
          <ExportButton activeTab={activeTab} />
          <PageSizeSelect />
          <OrderBySelect payerType={activeTab === "customers" ? PayerType.CUSTOMER : PayerType.OPERATOR} />
        </div>

        {activeTab === "customers" ? (
          <CustomersTable shouldRefetch={shouldRefetch} />
        ) : (
          <OperatorsTable shouldRefetch={shouldRefetch} />
        )}
      </Content>
    </ActionsContext>
  );
}

function PageSizeSelect() {
  const [value, setValue] = useState("10");
  const emit = useEmit<number>("payments-customers:page-size");

  const onValueChange = (value: string) => {
    setValue(value);
    emit(parseInt(value));
  };

  return (
    <div className="w-60 ml-auto">
      <Select onValueChange={onValueChange} value={value}>
        <SelectTrigger>
          <SelectValue placeholder="Page size" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="5">Show: 5 at once</SelectItem>
          <SelectItem value="10">Show: 10 at once</SelectItem>
          <SelectItem value="20">Show: 20 at once</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
